import { motion } from 'framer-motion';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ThemeContext } from 'styled-components';

import { ThemeType } from '~/theme/Themes';

interface AnimatedInputBorderProps {
  strokeWidth?: number;
  borderRadius?: number;
  onAnimationComplete?: () => void;
}

const buildPath = (w: number, h: number, r: number, sw: number): string => {
  const o = sw / 2;
  const ar = Math.max(0, r - o);
  return [
    `M ${o + ar},${o}`,
    `H ${w - o - ar}`,
    `Q ${w - o},${o} ${w - o},${o + ar}`,
    `V ${h - o - ar}`,
    `Q ${w - o},${h - o} ${w - o - ar},${h - o}`,
    `H ${o + ar}`,
    `Q ${o},${h - o} ${o},${h - o - ar}`,
    `V ${o + ar}`,
    `Q ${o},${o} ${o + ar},${o}`,
    'Z',
  ].join(' ');
};

const AnimatedInputBorder = ({
  strokeWidth = 1.5,
  borderRadius = 12,
  onAnimationComplete,
}: AnimatedInputBorderProps) => {
  const theme = useContext(ThemeContext) as ThemeType;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const gradId = useRef(`aib-grad-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    const parent = wrapperRef.current?.parentElement;
    if (!parent) return undefined;
    const measure = () => setSize({ w: parent.offsetWidth, h: parent.offsetHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    measure();
    return () => ro.disconnect();
  }, []);

  const { w, h } = size;
  const path = w && h ? buildPath(w, h, borderRadius, strokeWidth) : '';

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        top: -strokeWidth,
        left: -strokeWidth,
        right: -strokeWidth,
        bottom: -strokeWidth,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {path && (
        <svg width={w} height={h} style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}>
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={theme.colors.primary.c500} />
              <stop offset="100%" stopColor={theme.colors.aiOrange} />
            </linearGradient>
          </defs>
          <motion.path
            d={path}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            onAnimationComplete={onAnimationComplete}
          />
        </svg>
      )}
    </div>
  );
};

export default AnimatedInputBorder;
